import { useMemo } from 'react';
import { NORFOLK_SOUTHERN_CONSIST_REQUESTING } from '@rsos/capstone/src/constants/capabilities';
import { NORFOLK_SOUTHERN_CONSIST_REQUESTING_PERMISSION } from '@rsos/constants/permissions';
import { useBooleanFlagValue } from '@rsos/feature-flags';
import useHasCapability from '@rsos/hooks/useHasCapability';
import useHasPermission from '@rsos/hooks/useHasPermission';

/**
 * Datadog flag that gates the whole Norfolk Southern consist feature: the
 * train layer UX, the map card action, and the Resource Request Center
 * adapter. Off by default; the capability and permission still apply.
 */
export const NORFOLK_SOUTHERN_CONSIST_REQUESTS_FLAG =
  'norfolk-southern-consist-requests';

export interface NorfolkSouthernAccessConditions {
  isFlagEnabled: boolean;
  hasCapability: boolean;
  hasPermission: boolean;
}

/**
 * Each gate on the Norfolk Southern consist feature, separately, so UI can
 * report which one is failing (see ConditionalDisplay).
 */
export const useNorfolkSouthernAccessConditions =
  (): NorfolkSouthernAccessConditions => {
    const isFlagEnabled = useBooleanFlagValue(
      NORFOLK_SOUTHERN_CONSIST_REQUESTS_FLAG,
      false,
    );
    const capability = useHasCapability(NORFOLK_SOUTHERN_CONSIST_REQUESTING);
    const permission = useHasPermission(
      NORFOLK_SOUTHERN_CONSIST_REQUESTING_PERMISSION,
    );

    return useMemo(
      () => ({
        isFlagEnabled: Boolean(isFlagEnabled),
        hasCapability: Boolean(capability?.hasCapability),
        hasPermission: Boolean(permission?.hasPermission),
      }),
      [isFlagEnabled, capability, permission],
    );
  };

const useHasAccessToNorfolkSouthern = (): boolean => {
  const { isFlagEnabled, hasCapability, hasPermission } =
    useNorfolkSouthernAccessConditions();

  return isFlagEnabled && hasCapability && hasPermission;
};

export default useHasAccessToNorfolkSouthern;
